import { ChartCard, type ChartCardState } from "../../ChartCard";
import { Logo } from "../../../dashboard/Logo";
import { useAdminIntegrations } from "../../../../hooks/useAdminIntegrations";
import type { ConnectionStatus } from "../../../../types/integration";
import { connectedRows, type ConnectedRow } from "./integrationsPanelRows";
import { TenantAuthConfigControl } from "./TenantAuthConfigControl";
import styles from "./IntegrationsPanel.module.css";

type IntegrationsPanelProps = {
  readonly tenantId: string;
};

const STATUS_LABEL: Readonly<Record<ConnectionStatus, string>> = {
  active: "Active",
  pending: "Pending",
  error: "Error",
  revoked: "Revoked",
  disconnected: "Disconnected",
};

const STATUS_CLASS: Readonly<Record<ConnectionStatus, string | undefined>> = {
  active: styles.active,
  pending: styles.pending,
  error: styles.error,
  revoked: styles.error,
  disconnected: styles.muted,
};

const cardState = (
  status: "loading" | "ready" | "error",
  rows: ReadonlyArray<ConnectedRow>,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (rows.length === 0) return "empty";
  return "ready";
};

// Read-only view of the business-app integrations a tenant has connected.
// Sourced from the same admin endpoint the (removed) integrations modal used;
// channels stay in their own modal. IP-353: replaces the per-app pricing modal.
export const IntegrationsPanel = ({ tenantId }: IntegrationsPanelProps): JSX.Element => {
  const { status, integrations, error } = useAdminIntegrations(tenantId);
  const rows = connectedRows(integrations);
  const subtitle = rows.length > 0 ? `${rows.length} connected` : undefined;
  return (
    <>
    <ChartCard
      title="Connected integrations"
      subtitle={subtitle}
      wide
      state={cardState(status, rows)}
      emptyText="No integrations connected yet."
      errorText={error ?? undefined}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Integration</th>
            <th>Account</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ integration, connection }) => (
            <tr key={connection.connectionId}>
              <td>
                <div className={styles.integrationCell}>
                  <Logo
                    slug={integration.slug}
                    size={24}
                    brandHex={integration.iconBrandHex ?? null}
                    logoUrl={integration.ourLogoUrl ?? integration.logoUrl ?? null}
                  />
                  <div className={styles.titleCell}>
                    <span className={styles.name}>{integration.title}</span>
                    <span className={styles.category}>{integration.category}</span>
                  </div>
                </div>
              </td>
              <td>
                {connection.alias != null && connection.alias.length > 0 ? (
                  <span className={styles.account}>{connection.alias}</span>
                ) : (
                  <span className={styles.unlabeled}>Primary</span>
                )}
              </td>
              <td className={STATUS_CLASS[connection.status]}>
                {STATUS_LABEL[connection.status]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartCard>
    {/* IP-422: rendered outside the card so it's usable while the tenant has
        zero connections — the staged-rollout case it exists for. */}
    {status === "ready" ? (
      <TenantAuthConfigControl tenantId={tenantId} integrations={integrations} />
    ) : null}
    </>
  );
};
